import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Plus, FileText } from 'lucide-react';
import { toast } from 'sonner';
import { ChequesDataTable } from './components/ChequesDataTable';
import { getChequeColumns } from './components/columns';
import { listCheques, updateCheque, deleteCheque } from '@/app/services/chequeService';
import { ChequeRecord, ChequeStatus, ChequeRecordFilters } from '@/app/types/cheque';

const ChequesListPage: React.FC = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [filters, setFilters] = useState<ChequeRecordFilters>({
    limit: 500,
  });

  // Fetch cheques
  const { data: cheques = [], isLoading, isError, error } = useQuery<ChequeRecord[], Error>({
    queryKey: ['cheques', filters],
    queryFn: () => listCheques(filters),
  });

  // Update cheque mutation
  const updateChequeMutation = useMutation({
    mutationFn: ({ id, status }: { id: number; status: 'bounced' | 'cancelled' }) =>
      updateCheque(id, { status }),
    onSuccess: (_, variables) => {
      toast.success(`Cheque marked as ${variables.status}`);
      queryClient.invalidateQueries({ queryKey: ['cheques'] });
    },
    onError: (error: any) => {
      const errorMsg = error.response?.data?.detail || error.message || 'Failed to update cheque';
      toast.error(errorMsg);
    },
  });

  // Delete cheque mutation
  const deleteChequeMutation = useMutation({
    mutationFn: deleteCheque,
    onSuccess: () => {
      toast.success('Cheque deleted successfully');
      queryClient.invalidateQueries({ queryKey: ['cheques'] });
    },
    onError: (error: any) => {
      const errorMsg = error.response?.data?.detail || error.message || 'Failed to delete cheque';
      toast.error(errorMsg);
    },
  });

  // Handle clear cheque - navigate to payment-in with cheque data
  const handleClearCheque = (cheque: ChequeRecord) => {
    navigate(`/payments-in?cheque_id=${cheque.id}`);
  };

  // Handle status update
  const handleUpdateStatus = (id: number, status: 'bounced' | 'cancelled') => {
    if (window.confirm(`Are you sure you want to mark this cheque as ${status}?`)) {
      updateChequeMutation.mutate({ id, status });
    }
  };

  // Handle delete
  const handleDelete = (id: number) => {
    if (window.confirm('Are you sure you want to delete this cheque?')) {
      deleteChequeMutation.mutate(id);
    }
  };

  // Handle status filter
  const handleStatusFilter = (status: ChequeStatus | 'all') => {
    if (status === 'all') {
      const { status: _, ...rest } = filters;
      setFilters(rest);
    } else {
      setFilters({ ...filters, status });
    }
  };

  // Handle search
  const handleSearchChange = (search: string) => {
    if (search) {
      setFilters({ ...filters, search });
    } else {
      const { search: _, ...rest } = filters;
      setFilters(rest);
    }
  };

  const columns = getChequeColumns({
    onClearCheque: handleClearCheque,
    onUpdateStatus: handleUpdateStatus,
    onDelete: handleDelete,
  });

  // Calculate summary stats
  const stats = {
    total: cheques.length,
    pending: cheques.filter(c => c.status === ChequeStatus.PENDING).length,
    cleared: cheques.filter(c => c.status === ChequeStatus.CLEARED).length,
    bounced: cheques.filter(c => c.status === ChequeStatus.BOUNCED).length,
    totalAmount: cheques.reduce((sum, c) => sum + parseFloat(String(c.amount)) || 0, 0),
    pendingAmount: cheques
      .filter(c => c.status === ChequeStatus.PENDING)
      .reduce((sum, c) => sum + parseFloat(String(c.amount)) || 0, 0),
  };

  const formatCurrency = (amount: number): string => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
    }).format(amount);
  };

  if (isError) {
    return (
      <div className="container mx-auto py-6">
        <Card>
          <CardContent className="pt-6">
            <p className="text-destructive">
              Error loading cheques: {error instanceof Error ? error.message : 'Unknown error'}
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="container mx-auto py-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Cheque Register</h1>
          <p className="text-muted-foreground">Manage post-dated cheques and track clearances</p>
        </div>
        <Button onClick={() => navigate('/cheques/new')} className="gap-2">
          <Plus className="h-4 w-4" />
          Record Cheque
        </Button>
      </div>

      {/* Summary Cards */}
      <div className="grid gap-4 md:grid-cols-4">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Total Cheques</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.total}</div>
            <p className="text-xs text-muted-foreground">All recorded cheques</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Pending</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-orange-600">{stats.pending}</div>
            <p className="text-xs text-muted-foreground">Awaiting clearance</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Total Amount</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{formatCurrency(stats.totalAmount)}</div>
            <p className="text-xs text-muted-foreground">All cheques value</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Pending Amount</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-orange-600">
              {formatCurrency(stats.pendingAmount)}
            </div>
            <p className="text-xs text-muted-foreground">To be cleared</p>
          </CardContent>
        </Card>
      </div>

      {/* Data Table */}
      <Card>
        <CardHeader>
          <CardTitle>Cheques</CardTitle>
          <CardDescription>
            View and manage all recorded cheques. Click on "Clear Cheque" to convert to payment.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <ChequesDataTable
            columns={columns}
            data={cheques}
            isLoading={isLoading}
            onStatusFilter={handleStatusFilter}
            onSearchChange={handleSearchChange}
          />
        </CardContent>
      </Card>
    </div>
  );
};

export default ChequesListPage;